import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { PageTitle } from '../../../../_metronic/layout/core'
import { RecordsKeeperAIBreadCrumbs, RecordSync, storageIcons } from '../core/_constants'
import { InfoCard2 } from '../../widgets/components/UI/InfoCard2'
import { Col, Dropdown, Row } from 'react-bootstrap'
import { Link, useNavigate } from 'react-router-dom'
import { toAbsoluteUrl } from '../../../../_metronic/helpers'
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar'
import AddedStorageCard from '../components/SourceAndSync/addedStorage'
import AddStorageModal from '../components/SourceAndSync/addStorageModal'
import { useThemeMode } from '../../../../_metronic/partials'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { getAllSourcesList, getSourceReferenceData } from '../core/_requests'
import { useAuth } from '../../auth'
import { useDateFormat } from '../../../hooks/useDateFormat'
import { useTimeFormat } from '../../../hooks/useTimeFormat'
import Skeleton from 'react-loading-skeleton'
import { FileUpload } from '../../widgets/components/FileUpload'
import { allowedFileTypes } from '../../../core/_constants'
import { toast } from 'react-toastify'
import { useUpdateSettingsData } from '../../../hooks/updateSettingsData'
import threeDots from '../../../../_metronic/assets/images/svg/investor/threeDots.svg'

const SourcesAndSync = () => {
  const { formatMessage } = useIntl()
  const { mode } = useThemeMode()
  const [isModalActive, setIsModalActive] = useState(false)
  const { companyId } = useAuth()
  const [sources, setSources] = useState<any[]>([])
  const { getDateValue } = useDateFormat()
  const { getTimeValue } = useTimeFormat()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [isSourceLoading, setIsSourceLoading] = useState(false)
  const [modalStatus, setModalStatus] = useState(false)
  const [isReconnectClicked, setIsReconnectClicked] = useState(false)
  const [reconnectSourceIsClicked, setReconnectSourceIsClicked] = useState<RecordSync | null>(null)
  const [reconnectSourceIdIsClicked, setReconnectSourceIdIsClicked] = useState<string | null>(null)
  const [reconnectSourceNameIsClicked, setReconnectSourceNameIsClicked] = useState<string | null>(null)
  const [currentDocumentStep, setCurrentDocumentStep] = useState(1)
  const [storageSize, setStorageSize] = useState({
    sizeFromChargebee: 0,
    sizeFromChargebeeWithUnit: '',
    storageSize: 0,
    percentageUsed: '',
  })
  const { updateSettings } = useUpdateSettingsData()
  useEffect(() => {
    updateSettings()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchSources() {
    setIsSourceLoading(true)
    try {
      const {
        data: { success, data, errors },
      } = await getAllSourcesList(companyId!)
      if (success) {
        setSources(data.sources)
      } else {
        console.error(errors)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsSourceLoading(false)
    }
  }

  async function fetchSourcesInInterval() {
    try {
      const {
        data: { success, data, errors },
      } = await getAllSourcesList(companyId!)
      if (success) {
        setSources(data.sources)
      } else {
        console.error(errors)
      }
    } catch (error) {
      console.error(error)
    }
  }

  async function fetchIntervalStorageSize() {
    try {
      const {
        data: { success, data, errors },
      } = await getSourceReferenceData('recordKeeperStorageSize', companyId!)
      if (success) {
        setStorageSize(data)
      } else {
        console.error(errors)
      }
    } catch (error) {
      console.error(error)
    }
  }

  // Interval logic to call fetchSourcesInInterval every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      // Check if any source has noOfSyncFiles not equal to totalFiles
      const isSyncIncomplete = sources.filter(
        (source) => source.totalFiles === 0 || source.noOFSyncFiles !== source.totalFiles
      )

      if (isSyncIncomplete.length > 0) {
        fetchSourcesInInterval()
        fetchIntervalStorageSize()
      } else {
        clearInterval(interval) // Stop the interval if all files are synced
      }
    }, 10000) // 10 seconds interval

    return () => clearInterval(interval) // Cleanup interval on component unmount
  }, [sources]) // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchStorageSize() {
    setIsLoading(true)
    try {
      const {
        data: { success, data, errors },
      } = await getSourceReferenceData('recordKeeperStorageSize', companyId!)
      if (success) {
        setStorageSize(data)
      } else {
        console.error(errors)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchSources()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchStorageSize()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_DRIVE_CLIENT_ID as string}>
      <div>
        <PageTitle
          breadcrumbs={RecordsKeeperAIBreadCrumbs}
          description={formatMessage({ id: 'Storage' })}
        >
          {formatMessage({ id: 'RecordsKeeper AI' })}
        </PageTitle>

        <div className='px-6'>
          <InfoCard2
            title={'Managing Your Storage and Syncing Files'}
            desc={
              'This page allows you to manage your connected cloud storages and sync files seamlessly into RecordsKeeper. You can view, upload, and organize your records directly from various sources. Click "Link Cloud Storage" to link new storage or directory, and monitor sync progress in real-time.'
            }
            slug={
              'https://help.recordskeeper.ai/en/articles/10368743-how-to-manage-cloud-storage-and-file-synchronization-in-recordskeeper-ai'
            }
          />

          <div className='mb-4'>
            <Row className='mb-6'>
              {/* <Col md={6} className='mb-6 mb-md-0'>
                <div className='p-4 bg-white rounded-2 d-flex justify-content-between align-items-center h-100'>
                  <div className='d-flex gap-2'>
                    <div>
                      <img
                        src={toAbsoluteUrl('/media/logos/recordkeeper-logo.svg')}
                        alt=''
                        className='w-100'
                      />
                    </div>
                    <div>
                      <h3 className='mb-1 font-size-16'>
                        {formatMessage({id: 'Default Storage'})}
                      </h3>
                      <div className='d-flex gap-1 align-items-center mb-2'>
                        <img src={folderIcon} alt='' width={20} height={20} />
                        <p className='mb-0 font-size-12'>
                          <Link to={''} className='text-muted'>
                            {formatMessage({id: '/Records & Files'})}
                          </Link>
                        </p>
                      </div>

                      <div className='d-flex gap-1 align-items-center'>
                        <img
                          src={toAbsoluteUrl('/media/icons/RecordsKeeperAI/ic-storage.svg')}
                          alt=''
                          width={20}
                          height={20}
                        />
                        {isLoading || isSourceLoading ? (
                          <div className='mb-0 d-flex flex-column'>
                            <Skeleton
                              height={20}
                              width={60}
                              baseColor='var(--kt-skeleton-bg-color)'
                              highlightColor='var(--kt-skeleton-highlight-bg-color)'
                            />
                          </div>
                        ) : (
                          <p className=' text-muted mb-0 font-size-12'>
                            {storageSize.sizeFromChargebeeWithUnit
                              ? storageSize.sizeFromChargebeeWithUnit
                              : '0 GB'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div>
                    <button
                      className='btn btn-light'
                      onClick={() => {
                        if (!isLoading || !isSourceLoading) {
                          navigate('/rk/records-and-files')
                        }
                      }}
                    >
                      {formatMessage({id: 'View Records'})}
                    </button>
                  </div>
                </div>
              </Col> */}

              <Col md={12}>
                <div className='p-4 bg-white rounded-2 d-flex flex-md-row flex-column justify-content-between align-items-center gap-md-4 gap-2'>
                  <div className='d-flex gap-3 align-items-center'>
                    {isLoading || isSourceLoading ? (
                      <div>
                        <Skeleton
                          height={90}
                          width={90}
                          baseColor='var(--kt-skeleton-bg-color)'
                          highlightColor='var(--kt-skeleton-highlight-bg-color)'
                        />
                      </div>
                    ) : (
                      <div style={{ width: '20%' }}>
                        <CircularProgressbar
                          className='w-100'
                          value={parseInt(storageSize.percentageUsed)}
                          text={`${parseInt(storageSize.percentageUsed) ?? 0}%`}
                          strokeWidth={18}
                          styles={buildStyles({
                            strokeLinecap: 'round',
                            textSize: '16px',
                            pathTransitionDuration: 0.5,
                            pathColor: `#4776E6`,
                            textColor: mode === 'dark' ? 'White' : 'Black',
                            trailColor: mode === 'dark' ? '#4A6FA6' : '#D6ECFD',
                          })}
                        />
                      </div>
                    )}

                    <div className='d-flex flex-column gap-2'>
                      {isLoading || isSourceLoading ? (
                        <div>
                          <Skeleton
                            height={25}
                            width={60}
                            baseColor='var(--kt-skeleton-bg-color)'
                            highlightColor='var(--kt-skeleton-highlight-bg-color)'
                          />
                        </div>
                      ) : (
                        <h3 className='mb-0 font-size-20'>{`${storageSize && storageSize.storageSize ? storageSize.storageSize : 0
                          } / ${storageSize && storageSize.sizeFromChargebeeWithUnit
                            ? storageSize.sizeFromChargebeeWithUnit
                            : '0 GB'
                          }`}</h3>
                      )}

                      <p className='mb-0 text-muted font-size-12'>
                        {formatMessage({ id: 'Overall Available Storage' })}
                      </p>
                      <div className='d-flex align-items-center gap-1'>
                        {isLoading || isSourceLoading ? (
                          <div>
                            <Skeleton
                              height={20}
                              width={200}
                              baseColor='var(--kt-skeleton-bg-color)'
                              highlightColor='var(--kt-skeleton-highlight-bg-color)'
                            />
                          </div>
                        ) : (
                          <>
                            <p className='m-0'>{formatMessage({ id: 'Connected Storage' })}</p>
                            {sources.slice(0, 6).map((source) => {
                              return (
                                <div
                                  key={source.sourceId}
                                  className='d-flex gap-1 align-items-center'
                                >
                                  <img
                                    src={toAbsoluteUrl(
                                      `/media/icons/RecordsKeeperAI/${storageIcons[source.sourceType as RecordSync]
                                      }.svg`
                                    )}
                                    alt=''
                                    width={15}
                                    height={15}
                                  />
                                </div>
                              )
                            })}
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className=' d-flex flex-md-row flex-column gap-md-4 gap-2 w-md-auto w-100'>
                    <button
                      className='btn bg-light text-muted custom-button-property'
                      onClick={() => {
                        if (!isLoading || !isSourceLoading) {
                          navigate('/rk/records-and-files')
                        }
                      }}
                    >
                      {formatMessage({ id: 'View Records' })}
                    </button>
                    <button
                      className='btn bg-primary text-white custom-button-property'
                      onClick={() => navigate('/setting/billing-invoice?tab=storage-and-ai-usage')}
                    >
                      {formatMessage({ id: 'Buy More Storage' })}
                    </button>
                  </div>
                </div>
              </Col>
            </Row>

            <div className='p-4 bg-white rounded-2 d-flex flex-md-row flex-column justify-content-between align-items-center mb-4'>
              <div className='d-flex gap-3 mb-md-0 mb-2 align-items-center'>
                <div>
                  <img
                    src={toAbsoluteUrl('/media/icons/RecordsKeeperAI/ic-desktop-storage.svg')}
                    alt=''
                  />
                </div>
                <div>
                  <h3 className='mb-2 font-size-16'>{formatMessage({ id: 'Upload File' })}</h3>
                  <p className='mb-0 text-muted'>
                    {formatMessage({
                      id: 'The file will upload into the default storage of RecordsKeeper.AI',
                    })}
                  </p>
                </div>
              </div>
              <div className='w-100 w-md-auto'>
                <button
                  onClick={() => setModalStatus(true)}
                  className={
                    `btn w-100 custom-button-property ` +
                    (mode === 'dark'
                      ? `btn-outline-primary btn-no-hover-dark text-primary`
                      : `btn-outline-primary bg-new-conv-btn btn-no-hover text-primary`)
                  }
                >
                  {formatMessage({ id: 'Upload From Local' })}
                </button>
                <FileUpload
                  fileSize={parseInt(process.env.REACT_APP_RK_MAX_ZIP_FILE_SIZE as string)}
                  maxFileNumber={10}
                  allowType={allowedFileTypes}
                  metaData={{ module: 'document', isProtected: false }}
                  modalStatus={modalStatus}
                  handleClose={() => {
                    setModalStatus(false)
                  }}
                  handleSuccess={(id: number, name: string) => {
                    toast.success(
                      `${name
                        .split('/')
                        .pop()} uploaded successfully! Please wait for 1-2 minutes while the file is processed with AI. After that you can check the "Records & Files" section or "Overview" section`
                    )
                  }}
                  resourceType='all'
                  assemblyOption='rk-doc-upload'
                />
              </div>
            </div>
          </div>

          <h5 className='mb-0 mb-md-4'>{formatMessage({ id: 'Connected Cloud Storages' })}</h5>

          {/* button for Desktop view */}
          <div
            // className={`d-flex align-items-center justify-content-end position-absolute gap-2 gap-lg-3 d-none d-sm-block ${planStatus === 'payment_due' && 'planstatus-adjustment'}  ${planStatus === 'paid' && 'planstatus-not-adjustment'}`}
            className='d-flex align-items-center justify-content-end position-absolute gap-2 gap-lg-3 d-none d-sm-block'
            style={{ top: '80px', right: '30px' }}
          >
            <div className='d-flex flex-column mt-3 top-btn-main'>
              <div className='d-flex justify-content-end gap-4'>
                <button
                  type='button'
                  className='btn bg-primary custom-button-property font-size-13 text-white font-weight-400 w-100 d-flex align-items-center justify-content-center height-36'
                  onClick={() => setIsModalActive(true)}
                >
                  {formatMessage({ id: 'Link Cloud Storage' })}
                </button>
                <div className='d-flex align-items-center position-relative'>
                  <div className='dropdown-export-container'>
                    <Dropdown className='height-36'>
                      <Dropdown.Toggle className='btn btn-white table-three-dots dots-container'>
                        <img src={threeDots} alt='' className='three-dots-image' />
                      </Dropdown.Toggle>

                      <Dropdown.Menu className='m-1 rounded'>
                        <Dropdown.Item
                          className='text-dark ps-5 rounded'
                        >
                          <Link to='/rk/sources-and-sync/sync-log' className='text-dark'>
                            {formatMessage({ id: 'Sync Log' })}
                          </Link>
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* button for mobile view */}
          <div className='d-flex align-items-center gap-2 gap-lg-3 d-block d-sm-none'>
            <div className='d-flex flex-column mt-3 top-btn-main'>
              <div className='d-flex justify-content-md-end align-items-center gap-2 w-100 w-md-auto'>
                <button
                  type='button'
                  className='btn bg-primary custom-button-property font-size-13 text-white mb-4 font-weight-400 w-100 d-flex align-items-center justify-content-center height-36'
                  onClick={() => setIsModalActive(true)}
                >
                  {formatMessage({ id: 'Link Cloud Storage' })}
                </button>
                <div className='d-flex align-items-center position-relative'>
                  <div className='dropdown-export-container'>
                    <Dropdown className='height-36 mb-4'>
                      <Dropdown.Toggle className='btn btn-white table-three-dots dots-container'>
                        <img src={threeDots} alt='' className='three-dots-image' />
                      </Dropdown.Toggle>

                      <Dropdown.Menu className='m-1 rounded'>
                        <Dropdown.Item
                          className='text-dark ps-5 rounded'
                        >
                          <Link to='/rk/sources-and-sync/sync-log' className='text-dark'>
                            {formatMessage({ id: 'Sync Log' })}
                          </Link>
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {isLoading || isSourceLoading ? (
            <>
              {Array.from({ length: 2 }).map((_, index) => (
                <div className='d-flex flex-md-row flex-column justify-content-between gap-6'>
                  {Array.from({ length: 2 }).map((_, index) => (
                    <div className='p-4 bg-white rounded-2 mb-6 w-100'>
                      <div className='mb-4'>
                        <div className='d-flex flex-md-row flex-column justify-content-between gap-4'>
                          <Skeleton
                            width={48}
                            height={48}
                            baseColor='var(--kt-skeleton-bg-color)'
                            highlightColor='var(--kt-skeleton-highlight-bg-color)'
                            className='order-2 order-md-1'
                          />
                        </div>
                      </div>

                      <div className='d-flex gap-1 align-items-center mb-4'>
                        <Skeleton
                          width={20}
                          height={20}
                          baseColor='var(--kt-skeleton-bg-color)'
                          highlightColor='var(--kt-skeleton-highlight-bg-color)'
                        />
                        <Skeleton
                          width={60}
                          height={20}
                          baseColor='var(--kt-skeleton-bg-color)'
                          highlightColor='var(--kt-skeleton-highlight-bg-color)'
                        />
                      </div>

                      <div>
                        <div>
                          <Skeleton
                            width={80}
                            height={20}
                            baseColor='var(--kt-skeleton-bg-color)'
                            highlightColor='var(--kt-skeleton-highlight-bg-color)'
                          />{' '}
                        </div>
                        <div className='d-flex justify-content-between align-items-center'>
                          <div>
                            <Skeleton
                              width={120}
                              height={20}
                              baseColor='var(--kt-skeleton-bg-color)'
                              highlightColor='var(--kt-skeleton-highlight-bg-color)'
                            />
                            <div className='d-flex gap-1 align-items-center mb-2'>
                              <Skeleton
                                containerClassName='w-100'
                                style={{ width: '50%' }}
                                height={20}
                                baseColor='var(--kt-skeleton-bg-color)'
                                highlightColor='var(--kt-skeleton-highlight-bg-color)'
                              />
                            </div>
                          </div>
                        </div>

                        <div className='sources-sync-card align-items-center gap-2 w-100'>
                          <div className='col-6'>
                            <Skeleton
                              containerClassName='w-100'
                              style={{ width: '100%' }}
                              height={42}
                              baseColor='var(--kt-skeleton-bg-color)'
                              highlightColor='var(--kt-skeleton-highlight-bg-color)'
                            />
                          </div>

                          <div className='col-6'>
                            <Skeleton
                              containerClassName='w-100'
                              style={{ width: '100%' }}
                              height={42}
                              baseColor='var(--kt-skeleton-bg-color)'
                              highlightColor='var(--kt-skeleton-highlight-bg-color)'
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </>
          ) : (
            <>
              {(!isLoading || !isSourceLoading) && sources && sources.length === 0 ? (
                <div className='rounded bg-white rounded d-flex justify-content-center align-items-center'
                  style={{ minHeight: '500px' }}
                >
                  <div
                    className='w-100 h-100 bg-white d-flex align-items-center justify-content-center rounded-2 py-8'
                    style={{ padding: '100px 0' }}
                  >
                    <div className='text-center d-flex flex-column'>
                      <div>
                        <img
                          src={toAbsoluteUrl('/media/icons/investor-updates/No-storage-linked.svg')}
                          alt='no-data-img'
                          height={200}
                          width={200}
                        />
                      </div>
                      <div className='w-100'>
                        <h1 className='font-size-14'>
                          {formatMessage({ id: 'No Cloud Storage Linked' })}
                        </h1>
                        <p className='w-100'>
                          {formatMessage({
                            id: "You haven't added any cloud storage yet. Connect a service to back up and access your files easily!",
                          })}
                        </p>
                        <button
                          className='btn bg-primary text-white custom-button-property'
                          onClick={() => setIsModalActive(true)}
                        >
                          {formatMessage({ id: 'Link Cloud Storage' })}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <Row>
                  {sources.map((source, index) => (
                    <Col key={index} md={6}>
                      <AddedStorageCard
                        key={source.sourceId}
                        sourceId={source.sourceId}
                        sourceName={source.sourceType}
                        storageIcon={`/media/icons/RecordsKeeperAI/${storageIcons[source.sourceType as RecordSync]
                          }.svg`}
                        title={source.storageName}
                        storageSize={source.size}
                        syncdate={`${getDateValue(source.syncedDate)} ${getTimeValue(
                          source.syncedDate
                        )}`}
                        syncEnabled={source.syncStatus}
                        totalFiles={source.totalFiles}
                        noOfSyncFiles={source.noOFSyncFiles}
                        folderPath={source.folderPath}
                        attachedSourceEmail={source.attachedSourceEmail}
                        createdByEmail={source.sourceCreatedUserEmail}
                        createdByDate={`${getDateValue(source.createdAt)} ${getTimeValue(
                          source.createdAt
                        )}`}
                        fetchSources={fetchSources}
                        isFolder={source.isFolder}
                        fetchStorageSize={fetchStorageSize}
                        isPlanLimitExceeded={source.isPlanLimitExceeded}
                        isRetry={source.isRetry}
                        fetchIntervalSource={fetchSourcesInInterval}
                        isReconnectSource={source.isReconnectSource}
                        setIsModalActive={setIsModalActive}
                        setIsReconnectClicked={setIsReconnectClicked}
                        setReconnectSourceIsClicked={setReconnectSourceIsClicked}
                        setCurrentDocumentStep={setCurrentDocumentStep}
                        setReconnectSourceIdIsClicked={setReconnectSourceIdIsClicked}
                        setReconnectSourceNameIsClicked={setReconnectSourceNameIsClicked}

                      />
                    </Col>
                  ))}
                </Row>
              )}
            </>
          )}
        </div>
        <AddStorageModal
          isActive={isModalActive}
          setIsActive={setIsModalActive}
          fetchSources={fetchSources}
          isReconnectClicked={isReconnectClicked}
          reconnectSourceIsClicked={reconnectSourceIsClicked}
          currentDocumentStep={currentDocumentStep}
          setCurrentDocumentStep={setCurrentDocumentStep}
          reconnectSourceIdIsClicked={reconnectSourceIdIsClicked}
          reconnectSourceNameIsClicked={reconnectSourceNameIsClicked}
          setIsReconnectClicked={setIsReconnectClicked}
        />
      </div>
    </GoogleOAuthProvider>
  )
}

export default SourcesAndSync
